<script setup>
import { computed, ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useHttp } from '../composables/http'
import { useWebsites } from '@webapp/composables/websites'
import { usePages } from '@webapp/composables/pages'
import { useElements } from '@webapp/composables/elements'
import ElementRender from '@webapp/components/Elements/ControlElements/ElementRender.vue'
import AppHeader from './AppHeader.vue'

const store = useStore()
const $http = useHttp()

const pageId = computed(() => store.getters['onboarding/pageId'])
const websiteId = computed(() => store.getters['onboarding/websiteId'])

const { website, getWebsite } = useWebsites()
const { page, getPage } = usePages()
const { getElementsFromStore } = useElements()

const loading = ref(true)
const elements = computed(() => getElementsFromStore())

const pageStyle = computed(() => {
    const ps = page.value?.style
    if (ps && ps.backgroundImage) {
        return { ...ps, backgroundImage: `url("${ps.backgroundImage}")` }
    }
    return ps || {}
})

const websiteClass = computed(() => {
    return 'webapp-website-body ' + (website.value?.properties?.palette || '')
})

const websiteDomain = computed(() => import.meta.env.PUBLIC_WEBSITE_DOMAIN || 'timebrella.com')
const siteUrl = computed(() => {
    const domain = website.value?.domain
    if (!domain) return ''
    return `${domain}.${websiteDomain.value}`
})

onMounted(async () => {
    const wId = websiteId.value
    const pId = pageId.value
    console.log('DashboardComponent mounted, websiteId:', wId, 'pageId:', pId)

    if (!wId || !pId) {
        console.warn('Missing websiteId or pageId from onboarding store')
        loading.value = false
        return
    }

    try {
        await getWebsite(wId)
        await getPage(wId, pId)
        const { data } = await $http.get(`/pages/${pId}/elements`)
        const sorted = [...(data.data || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        const needsOrderFix = sorted.some((el, i) => el.order == null || el.order !== i + 1)
        sorted.forEach((el, i) => { el.order = i + 1 })
        store.commit('elements/SET_ELEMENTS', sorted)
        store.commit('elements/SET_PAGE_ID', pId)

        if (needsOrderFix) {
            const resources = {}
            sorted.forEach(el => { resources[el.id] = el })
            await $http.patch(`pages/${pId}/elements/batch`, { resources })
        }
    } catch (e) {
        console.error('Failed to load page elements', e)
    } finally {
        loading.value = false
    }
})

</script>

<template>
    <div class="mp-app">
        <AppHeader />

        <div v-if="siteUrl" class="mp-address-bar">
            <div class="mp-address-bar-inner">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mp-address-bar-icon">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
                <a :href="'https://' + siteUrl" target="_blank" class="mp-address-bar-url">{{ siteUrl }}</a>
            </div>
        </div>

        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border" role="status"></div>
        </div>

        <div v-else :class="websiteClass" :style="pageStyle">
            <div class="elements-container d-flex flex-wrap">
                <div
                    v-for="(element, index) in elements"
                    :key="element.id"
                    class="element-block"
                    :class="element.style?.additional?.popup?.elementWidth || 'w-100'"
                >
                    <ElementRender
                        :key="element.id"
                        :type="element.type"
                        :data="{ ...element }"
                        :pageId="String(pageId)"
                        :websiteId="String(websiteId)"
                        :prev="index - 1 >= 0 ? elements[index - 1] : undefined"
                        :next="index + 1 < elements.length ? elements[index + 1] : undefined"
                        :hide-style-copy="true"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.webapp-website-body {
    padding: 0 0 80px 0;
    min-height: calc(100vh - 58px);

    .elements-container {
        width: 100%;
    }

    .element {
        min-height: 20px;
    }

    .element-block {
        min-height: 10px;

        &.w-33 {
            width: 33.333333%;
            flex: 0 0 33.333333%;
            max-width: 33.333333%;
            vertical-align: top;
        }

        &.w-50 {
            width: 50%;
            flex: 0 0 50%;
            max-width: 50%;
            vertical-align: top;
        }

        &.w-100 {
            width: 100%;
            flex: 0 0 100%;
            max-width: 100%;
        }
    }

    .btn-add-element,
    .copy-element,
    .btn-delete-wrapper {
        display: none !important;
    }
}

.mp-address-bar {
    position: sticky;
    top: 53px;
    z-index: 799;
    background: #f0f0f0;
    border-bottom: 1px solid #d1d5db;
    padding: 6px 12px;
}

.mp-address-bar-inner {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 13px;
}

.mp-address-bar-icon {
    color: #6b7280;
    flex-shrink: 0;
}

.mp-address-bar-url {
    color: #333;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
        color: #0d6efd;
        text-decoration: underline;
    }
}
</style>
